/**
 * Permission codes the Accounts Receivable screens declare an interest in.
 *
 * They are the dot-strings `internal/ar/permissions.go` registers with the RBAC
 * catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once here
 * means a renamed permission is a compile error rather than a silently invisible
 * menu item.
 *
 * `invoiceConfirm` and `creditOverride` are the pair that matters most on these
 * screens. Confirming an AR invoice runs §8.4's credit check; pushing a document
 * **past** that check is a different decision made by a different person, and the
 * server keeps it behind its own permission which no baseline role holds. The
 * document screen therefore renders "Confirm" and "Request a credit override" as
 * two independently permissioned actions and never assumes one implies the other
 * — a user with confirm alone sees the block explained and no override button,
 * which is the honest rendering of what they may do.
 */

export const AR_PERMISSIONS = {
  customerRead: 'ar.customer.read',
  customerCreate: 'ar.customer.create',
  customerUpdate: 'ar.customer.update',
  customerDelete: 'ar.customer.delete',

  invoiceRead: 'ar.invoice.read',
  invoiceCreate: 'ar.invoice.create',
  invoiceUpdate: 'ar.invoice.update',
  invoiceDelete: 'ar.invoice.delete',
  invoiceConfirm: 'ar.invoice.confirm',
  invoicePost: 'ar.invoice.post',
  /** Confirm a document the credit or overdue check refused. */
  creditOverride: 'ar.invoice.credit_override',

  creditNoteRead: 'ar.credit_note.read',
  creditNoteCreate: 'ar.credit_note.create',
  creditNoteUpdate: 'ar.credit_note.update',
  creditNoteDelete: 'ar.credit_note.delete',
  creditNotePost: 'ar.credit_note.post',

  receiptRead: 'ar.receipt.read',
  receiptCreate: 'ar.receipt.create',
  receiptAllocate: 'ar.receipt.allocate',

  reportRead: 'ar.report.read',
  /** `GET /ar-dunning-levels`, `/ar-dunning-events` and `/reports/dunning-effectiveness`. */
  dunningRead: 'ar.dunning.read',
  /** Run the dunning cycle, record a response, pause and resume a customer. */
  dunningManage: 'ar.dunning.manage',
  /** Create, edit and delete a dunning level (the schedule itself). */
  dunningLevelManage: 'ar.dunning.level.manage',

  /* ---------------------------------- C3 expected credit loss (§6.1) */

  eclRead: 'ar.ecl.read',
  eclManage: 'ar.ecl.manage',
  /**
   * Step-up MFA. `ar.ecl.manage` ⊗ `ar.ecl.post` is a declared SoD pair: the
   * loss rates and the provision they produce are two hands.
   */
  eclPost: 'ar.ecl.post',

  /** Read the chart of accounts to offer revenue accounts as line choices. */
  accountRead: 'org.account.read',
  /** Read companies to scope a document or a report. */
  companyRead: 'org.company.read',
  /** Read the dimension trees behind the per-line cost centre / branch pickers. */
  unitRead: 'org.unit.read',
  /** Read bank and cash accounts to choose where a receipt was deposited. */
  bankAccountRead: 'cash.account.read',
} as const

export type ArPermission = (typeof AR_PERMISSIONS)[keyof typeof AR_PERMISSIONS]
